
import { Location, LocationStrategy } from '@angular/common';
import { Injectable } from '@angular/core';

const INITAL_STATE = 1;

function newStateId(key: string) {
	const historyStateIdStr = sessionStorage.getItem(key);
	const newHistoryStateId = !historyStateIdStr ? INITAL_STATE : Number(historyStateIdStr) + 1;
	sessionStorage.setItem(key, String(newHistoryStateId));
	return newHistoryStateId;
}

function initStateId(key: string) {
	const historyStateIdStr = sessionStorage.getItem(key);
	if (!historyStateIdStr) {
		sessionStorage.setItem(key, String(INITAL_STATE));
	}
}

interface CustomState {
	copoStateId: number;
	copoGlobalStateId: number;
}

@Injectable({ providedIn: 'root' })
export class CustomLocation extends Location {
	static readonly HISTORY_STATE_ID_KEY = 'copoHistoryStateId';
	static readonly HISTORY_GLOBAL_STATE_ID_KEY = 'copoHistoryGlobalStateId';

	static GET_CURRENT_STATE_ID() {
		const { state } = <{ state: unknown }> history;
		if (state === null || typeof state !== 'object') {
			return INITAL_STATE;
		}
		const { copoStateId } = <Partial<CustomState>> state;
		if (typeof copoStateId !== 'number') {
			return INITAL_STATE;
		}
		return copoStateId;
	}

	static GET_CURRENT_GLOBAL_STATE_ID() {
		const { state } = <{ state: unknown }> history;
		if (state === null || typeof state !== 'object') {
			return INITAL_STATE;
		}
		const { copoGlobalStateId } = <Partial<CustomState>> state;
		if (typeof copoGlobalStateId !== 'number') {
			return INITAL_STATE;
		}
		return copoGlobalStateId;
	}

	static IS_INITIAL_GLOBAL_STATE() {
		return CustomLocation.GET_CURRENT_GLOBAL_STATE_ID() === INITAL_STATE;
	}

	static SET_STATE(storeName: string, stateId: number, state: unknown) {
		sessionStorage.setItem(`${ storeName }-${ stateId }`, JSON.stringify(state));
	}

	static GET_STATE(storeName: string, stateId: number) {
		const stateStr = sessionStorage.getItem(`${ storeName }-${ stateId }`);
		if (!stateStr) {
			return null;
		}
		const state = <unknown> JSON.parse(stateStr);
		return typeof state === 'object' ? state : null;
	}

	constructor(platformStrategy: LocationStrategy) {
		super(platformStrategy);
		initStateId(CustomLocation.HISTORY_GLOBAL_STATE_ID_KEY);
		initStateId(CustomLocation.HISTORY_STATE_ID_KEY);
	}

	override go(path: string, query = '', state?: object) {
		const newState = {
			...state,
			copoGlobalStateId: newStateId(CustomLocation.HISTORY_GLOBAL_STATE_ID_KEY),
			copoStateId: newStateId(CustomLocation.HISTORY_STATE_ID_KEY)
		};
		super.go(path, query, newState);
	}

	override replaceState(path: string, query = '', state?: object) {
		const newState = {
			...<object> history.state,
			...state,
			copoGlobalStateId: CustomLocation.GET_CURRENT_GLOBAL_STATE_ID(),
			// keep the same state if the path is unchanged
			copoStateId: this.isCurrentPathEqualTo(path)
				? CustomLocation.GET_CURRENT_STATE_ID()
				: newStateId(CustomLocation.HISTORY_STATE_ID_KEY)
		};
		super.replaceState(path, query, newState);
	}
}
